import { useDocumentTitle } from "@/hooks/use-document-title";
import { useCallback, useState, useEffect, type FC } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { MembersTab } from "@/components/settings/organization/members/MembersTab";
import { GroupsTab } from "@/components/settings/organization/groups/GroupsTab";
import { ServiceAccountsTab } from "@/components/settings/organization/service-accounts/ServiceAccountsTab";
import { CreateGroupDialog } from "@/components/settings/organization/groups/CreateGroupDialog";
import { CreateServiceAccountDialog } from "@/components/settings/organization/service-accounts/CreateServiceAccountDialog";
import { serviceAccountDetailsUrl } from "@/utils/service-accounts";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { Button } from "@/components/flexkit/Button";
import { useNavigate } from "react-router-dom";
import { IconUser } from "@/assets/icons/geist/IconUser";
import { IconUsers } from "@/assets/icons/geist/IconUsers";
import { IconUserAdd } from "@/assets/icons/geist/IconUserAdd";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import IconCuteRobotBase from "central-icons/IconCuteRobot";
import { withGitpodSize } from "@/assets/icons/utils";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

const IconCuteRobot = withGitpodSize(IconCuteRobotBase);

export const MembersPage: FC = () => {
    useDocumentTitle("Members");
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { hasPermission, isOrgAdmin } = useResourcePermissions();
    const { membership } = useMembership();
    const { data: organization } = useOrganization();
    const location = useLocation();
    const [selectedTab, setSelectedTab] = useState<"members" | "groups" | "service-accounts">(
        location.hash === "#groups" ? "groups" : location.hash === "#service-accounts" ? "service-accounts" : "members",
    );
    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
    const [showCreateServiceAccountDialog, setShowCreateServiceAccountDialog] = useState(false);
    const navigate = useNavigate();

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;
    const canCreateGroup = hasPermission(ResourceType.ORGANIZATION, membership?.organizationId || "", "group:create");
    const canCreateServiceAccount = hasPermission(
        ResourceType.ORGANIZATION,
        membership?.organizationId || "",
        "serviceaccount:create",
    );

    // Handle hash anchor navigation
    useEffect(() => {
        if (location.hash === "#groups") {
            setSelectedTab("groups");
        } else if (location.hash === "#service-accounts") {
            setSelectedTab("service-accounts");
        } else if (location.hash === "#people") {
            setSelectedTab("members");
        }
    }, [location.hash]);

    const onInviteMembers = useCallback(() => {
        navigate("invite", { replace: true });
    }, [navigate]);

    const onAddGroup = useCallback(() => {
        setShowCreateGroupDialog(true);
    }, []);

    const onAddServiceAccount = useCallback(() => {
        setShowCreateServiceAccountDialog(true);
    }, []);

    const handleTabChange = useCallback(
        (value: string) => {
            const newTab = value as "members" | "groups" | "service-accounts";
            setSelectedTab(newTab);
            // Update URL hash to reflect the selected tab
            const hash = newTab === "members" ? "#people" : newTab === "groups" ? "#groups" : "#service-accounts";
            navigate(`${location.pathname}${hash}`, { replace: true });
        },
        [navigate, location.pathname],
    );

    if (!isGroupManagementEnabled) {
        return <MembersTab />;
    }

    return (
        <Tabs value={selectedTab} className="mt-4 w-full" onValueChange={handleTabChange}>
            <div className="mb-4 flex w-full flex-row justify-between">
                <TabsList className="w-fit">
                    <TabsTrigger value="members" LeadingIcon={IconUser}>
                        People
                    </TabsTrigger>
                    <TabsTrigger value="groups" LeadingIcon={IconUsers}>
                        Groups
                    </TabsTrigger>
                    <TabsTrigger value="service-accounts" LeadingIcon={IconCuteRobot}>
                        Service Accounts
                    </TabsTrigger>
                </TabsList>
                {selectedTab === "members" && isOrgAdmin && (
                    <Button
                        size="sm"
                        onClick={onInviteMembers}
                        LeadingIcon={IconUserAdd}
                        data-tracking-id="invite-members-page"
                    >
                        Invite
                    </Button>
                )}
                {selectedTab === "groups" && canCreateGroup && isEnterpriseTier && (
                    <Button
                        size="sm"
                        onClick={onAddGroup}
                        LeadingIcon={IconPlus}
                        data-tracking-id="new-group-members-page"
                    >
                        New Group
                    </Button>
                )}
                {selectedTab === "service-accounts" && canCreateServiceAccount && (
                    <Button
                        size="sm"
                        onClick={onAddServiceAccount}
                        LeadingIcon={IconPlus}
                        data-tracking-id="new-service-account-members-page"
                    >
                        New Service Account
                    </Button>
                )}
            </div>
            <TabsContent value="members">
                <MembersTab />
            </TabsContent>
            <TabsContent value="groups">
                <GroupsTab onCreateGroup={() => setShowCreateGroupDialog(true)} canCreateGroup={canCreateGroup} />
            </TabsContent>
            <TabsContent value="service-accounts">
                <ServiceAccountsTab
                    onCreateServiceAccount={() => setShowCreateServiceAccountDialog(true)}
                    canCreateServiceAccount={canCreateServiceAccount}
                />
            </TabsContent>
            <CreateGroupDialog open={showCreateGroupDialog} onClose={() => setShowCreateGroupDialog(false)} />
            <CreateServiceAccountDialog
                open={showCreateServiceAccountDialog}
                onClose={() => setShowCreateServiceAccountDialog(false)}
                onSuccess={(serviceAccountId) => navigate(serviceAccountDetailsUrl({ id: serviceAccountId }))}
            />
        </Tabs>
    );
};
